import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Bell,
  Bot,
  Check,
  CheckCheck,
  Clock,
  ImageOff,
  Loader2,
  MessageSquareText,
  Send,
  Trash2,
  User,
} from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAlertPrefs } from "../alerts";
import type { WhatsAppChatMessage, WhatsAppConversation } from "../api";
import {
  useConversationMessages,
  useConversations,
  useDeleteConversation,
  useSendMessage,
  useToggleAi,
} from "../hooks";

function timeLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function StatusTicks({ msg }: { msg: WhatsAppChatMessage }) {
  if (msg.direction !== "outbound") return null;
  if (msg.status === "failed")
    return <AlertCircle className="size-3.5 text-destructive" />;
  if (msg.status === "read")
    return <CheckCheck className="size-3.5 text-sky-400" />;
  if (msg.status === "delivered") return <CheckCheck className="size-3.5" />;
  if (msg.status === "sent") return <Check className="size-3.5" />;
  return <Clock className="size-3.5" />;
}

function MessageBubble({ msg }: { msg: WhatsAppChatMessage }) {
  const out = msg.direction === "outbound";
  const isImage = !!msg.media_url;
  const [imgBroken, setImgBroken] = useState(false);
  return (
    <div className={cn("flex", out ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm",
          out
            ? "rounded-br-sm bg-primary text-primary-foreground"
            : "rounded-bl-sm border bg-card",
        )}
      >
        {isImage &&
          (imgBroken ? (
            <div className="flex items-center gap-2 py-2 text-xs opacity-70">
              <ImageOff className="size-4" /> Photo unavailable
            </div>
          ) : (
            <img
              src={msg.media_url}
              alt="attachment"
              loading="lazy"
              onError={() => setImgBroken(true)}
              className="mb-1 max-h-72 max-w-full rounded-lg"
            />
          ))}
        {!isImage && msg.message_type !== "text" && (
          <div className="text-xs italic opacity-70">
            [{msg.message_type} message]
          </div>
        )}
        {msg.body && <div className="whitespace-pre-wrap">{msg.body}</div>}
        <div
          className={cn(
            "mt-1 flex items-center justify-end gap-1 text-[10px]",
            out ? "text-primary-foreground/70" : "text-muted-foreground",
          )}
        >
          {out && (
            <span className="flex items-center gap-0.5">
              {msg.sender === "ai" ? (
                <Bot className="size-3" />
              ) : (
                <User className="size-3" />
              )}
            </span>
          )}
          {timeLabel(msg.created_at)}
          <StatusTicks msg={msg} />
        </div>
        {msg.status === "failed" && msg.error && (
          <div className="mt-1 text-[11px] text-destructive">{msg.error}</div>
        )}
      </div>
    </div>
  );
}

function ConversationRow({
  c,
  active,
  onClick,
  onDelete,
}: {
  c: WhatsAppConversation;
  active: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  // A div wrapping two buttons, not one big button: a delete control nested
  // inside the row button would be invalid markup and unreachable by keyboard.
  return (
    <div
      className={cn(
        "group relative flex items-center border-b transition-colors hover:bg-accent",
        active && "bg-accent",
      )}
    >
      <button
        onClick={onClick}
        className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3 text-left"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
          {(c.customer_name || c.customer_number).replace("+", "").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium">
              {c.customer_name || c.customer_number}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {timeLabel(c.last_message_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs text-muted-foreground">
              {c.last_message_preview || c.customer_number}
            </span>
            {c.unread_count > 0 && (
              <span className="flex size-4.5 min-w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold text-white">
                {c.unread_count}
              </span>
            )}
          </div>
        </div>
      </button>
      <button
        onClick={onDelete}
        aria-label={`Delete conversation with ${c.customer_name || c.customer_number}`}
        className="mr-1.5 rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

function AlertSettings() {
  const { soundEnabled, onlyWhenAiOff, setSoundEnabled, setOnlyWhenAiOff } =
    useAlertPrefs();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="shrink-0 gap-2">
          <Bell className="size-4" />
          Alerts
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>New-message alerts</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={soundEnabled}
          onCheckedChange={(v) => setSoundEnabled(!!v)}
          onSelect={(e) => e.preventDefault()}
        >
          Play a sound
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={onlyWhenAiOff}
          onCheckedChange={(v) => setOnlyWhenAiOff(!!v)}
          onSelect={(e) => e.preventDefault()}
        >
          Only when AI replies are off
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ConversationsPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const listQ = useConversations();
  const messagesQ = useConversationMessages(activeId);
  const sendMut = useSendMessage(activeId);
  const toggleMut = useToggleAi(activeId);
  const deleteMut = useDeleteConversation();
  const bottomRef = useRef<HTMLDivElement>(null);
  // The thread queued for deletion — held so the dialog can name the contact.
  const [pendingDelete, setPendingDelete] = useState<WhatsAppConversation | null>(
    null,
  );

  const conversations = listQ.data ?? [];
  const active = conversations.find((c) => c.id === activeId) ?? null;
  const messages = messagesQ.data ?? [];

  function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    deleteMut.mutate(target.id, {
      onSuccess: () => {
        // Close the pane if we just deleted what it was showing.
        setActiveId((current) => (current === target.id ? null : current));
        setPendingDelete(null);
        toast.success("Conversation deleted");
      },
      onError: () => toast.error("Couldn't delete the conversation."),
    });
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, activeId]);

  function handleSend() {
    const body = draft.trim();
    if (!body || !activeId || sendMut.isPending) return;
    sendMut.mutate(body, {
      onSuccess: () => setDraft(""),
      onError: () => toast.error("Couldn't send — WhatsApp rejected the message."),
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Conversations</h1>
          <p className="text-sm text-muted-foreground">
            WhatsApp chats with your customers. The assigned agent replies
            automatically — switch AI off on a thread to take over yourself.
          </p>
        </div>
        <AlertSettings />
      </div>

      <div className="flex h-[calc(100vh-190px)] min-h-[420px] overflow-hidden rounded-xl border bg-card">
        {/* Thread list */}
        <div className="flex w-full max-w-xs shrink-0 flex-col border-r">
          <div className="border-b px-3 py-2.5 text-sm font-medium">
            Chats{" "}
            <span className="text-muted-foreground">
              ({conversations.length})
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {listQ.isLoading ? (
              <div className="space-y-3 p-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
                <MessageSquareText className="size-8 opacity-40" />
                No conversations yet. When customers message your WhatsApp
                number, chats appear here.
              </div>
            ) : (
              conversations.map((c) => (
                <ConversationRow
                  key={c.id}
                  c={c}
                  active={c.id === activeId}
                  onClick={() => setActiveId(c.id)}
                  onDelete={() => setPendingDelete(c)}
                />
              ))
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="flex min-w-0 flex-1 flex-col">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
              <MessageSquareText className="size-10 opacity-30" />
              Select a conversation
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {active.customer_name || active.customer_number}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {active.customer_number}
                    {active.agent_name ? ` · Agent: ${active.agent_name}` : ""}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <Bot className="size-3.5" />
                    AI replies
                    <Switch
                      checked={active.ai_enabled}
                      disabled={toggleMut.isPending}
                      onCheckedChange={(v) =>
                        toggleMut.mutate(v, {
                          onSuccess: () =>
                            toast.success(
                              v
                                ? "AI will reply automatically"
                                : "AI paused — you're replying manually",
                            ),
                          onError: () => toast.error("Couldn't update the AI toggle."),
                        })
                      }
                    />
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete conversation"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setPendingDelete(active)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto bg-muted/30 p-4">
                {messagesQ.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-2/3" />
                    <Skeleton className="ml-auto h-10 w-1/2" />
                    <Skeleton className="h-10 w-1/2" />
                  </div>
                ) : (
                  messages.map((m) => <MessageBubble key={m.id} msg={m} />)
                )}
                <div ref={bottomRef} />
              </div>

              <div className="flex items-end gap-2 border-t p-3">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    active.ai_enabled
                      ? "Type to jump in (AI keeps replying unless you switch it off)…"
                      : "Type a reply…"
                  }
                  className="max-h-32 min-h-[40px] flex-1 resize-none"
                  rows={1}
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!draft.trim() || sendMut.isPending}
                >
                  <Send className="size-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete the chat with{" "}
              {pendingDelete?.customer_name || pendingDelete?.customer_number}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the thread and its whole message history from this
              dashboard, and can't be undone. Nothing is deleted from the
              customer's phone — if they message again, a new chat starts here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMut.isPending}
              onClick={(e) => {
                // Keep the dialog up while the request is in flight, so the
                // row can't be clicked again mid-delete.
                e.preventDefault();
                confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMut.isPending && <Loader2 className="size-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
